import type { TFunction } from "i18next";

/** Internal error tokens persisted by the preflight worker — never show raw to tenants. */
const INTERNAL_ERROR_CODES = new Set([
  "TRANSIENT_ANALYZER_FAILURE",
  "DS_ANALYZER_CALL_FAILED",
]);

function isInternalCode(value: string): boolean {
  return INTERNAL_ERROR_CODES.has(value) || /^[A-Z][A-Z0-9_]+$/.test(value);
}

/**
 * Maps persisted analyzer/storage errors to tenant-safe copy. User-facing messages
 * (e.g. codec guidance from DS) pass through unchanged.
 */
export function formatAssetError(
  raw: string | null | undefined,
  t: TFunction,
): string {
  const normalized = raw?.trim();
  if (!normalized) {
    return t("preflight.assetError.generic");
  }

  if (
    normalized === "TRANSIENT_ANALYZER_FAILURE" ||
    normalized === "DS analyzer call failed"
  ) {
    return t("preflight.assetError.transientAnalyzer");
  }

  if (normalized === "Upload not found") {
    return t("preflight.assetError.uploadNotFound");
  }

  if (normalized.startsWith("DS could not analyze")) {
    return t("preflight.assetError.analyzerRejected");
  }

  if (normalized.startsWith("URL could not be fetched")) {
    return t("preflight.assetError.urlFetch");
  }

  if (normalized === "Failed to sign asset URL") {
    return t("preflight.assetError.storage");
  }

  if (isInternalCode(normalized)) {
    return t("preflight.assetError.generic");
  }

  return normalized;
}
